import {ChevronDownIcon, ChevronRightIcon, PlusIcon, TrashIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, IconButton} from '@primer/react'
import {clsx} from 'clsx'
import {useCallback, useMemo, useState} from 'react'

import type {ActionConfig, ActionType} from '../config-parser'
import {ACTION_METADATA, ALL_ACTION_TYPES} from '../config-parser'

import {AutocompleteTokenTagInput} from './AutocompleteTokenTagInput'
import type {FetchSuggestions} from './AutocompleteTokenTagInput'
import styles from './ConfigSections.module.css'
import {SectionHeader} from './SectionHeader'
import {createIssueFieldFetcher, createIssueTypeFetcher, createLabelFetcher} from './fetch-suggestions'

type ActionsSectionProps = {
  actions: ActionConfig[]
  commentEnabled: boolean
  onChange: (actions: ActionConfig[]) => void
  onCommentEnabledChange: (enabled: boolean) => void
  repoOwner: string
  repoName: string
}

export function ActionsSection({
  actions,
  commentEnabled,
  onChange,
  onCommentEnabledChange,
  repoOwner,
  repoName,
}: ActionsSectionProps) {
  const fetchers = useMemo<Partial<Record<ActionType, FetchSuggestions>>>(() => {
    return {
      'add-labels': createLabelFetcher(repoOwner, repoName),
      'add-type': createIssueTypeFetcher(repoOwner, repoName),
      'add-fields': createIssueFieldFetcher(repoOwner, repoName),
    }
  }, [repoOwner, repoName])

  const searchText: Record<ActionType, {placeholder: string; emptyText: string}> = {
    'add-labels': {placeholder: 'Search labels...', emptyText: 'No labels found'},
    'add-type': {placeholder: 'Search types...', emptyText: 'No types found'},
    'add-fields': {placeholder: 'Search fields...', emptyText: 'No fields found'},
  }

  const [expandedAction, setExpandedAction] = useState<ActionType | null>(
    () =>
      actions.find(a => a.enabled && ((a.allowed && a.allowed.length > 0) || (a.blocked && a.blocked.length > 0)))
        ?.type ?? null,
  )

  const enabledActionTypes = useMemo(() => new Set(actions.filter(a => a.enabled).map(a => a.type)), [actions])
  const availableActions = useMemo(
    () => ALL_ACTION_TYPES.filter(type => !enabledActionTypes.has(type)),
    [enabledActionTypes],
  )
  const canAddMore = availableActions.length > 0 || !commentEnabled

  const addAction = useCallback(
    (type: ActionType) => {
      onChange(actions.map(a => (a.type === type ? {...a, enabled: true} : a)))
    },
    [actions, onChange],
  )

  const removeAction = useCallback(
    (type: ActionType) => {
      onChange(actions.map(a => (a.type === type ? {...a, enabled: false, allowed: undefined, blocked: undefined} : a)))
      setExpandedAction(prev => (prev === type ? null : prev))
    },
    [actions, onChange],
  )

  const updateAction = useCallback(
    (type: ActionType, updates: Partial<ActionConfig>) => {
      onChange(actions.map(a => (a.type === type ? {...a, ...updates} : a)))
    },
    [actions, onChange],
  )

  const toggleAdvanced = useCallback((type: ActionType) => {
    setExpandedAction(prev => (prev === type ? null : type))
  }, [])

  const anyEnabled = commentEnabled || actions.some(a => a.enabled)
  const enabledActions = actions.filter(a => a.enabled)
  const showList = commentEnabled || enabledActions.length > 0

  return (
    <div className={styles.section}>
      <SectionHeader
        title="Actions"
        subtitle={
          <>
            Configure the actions the agent can take.
            {!anyEnabled && <span className={styles.validationInline}> Select at least one action.</span>}
          </>
        }
        action={
          <ActionMenu>
            <ActionMenu.Anchor>
              <IconButton
                icon={PlusIcon}
                aria-label="Add action"
                size="small"
                data-testid="add-action-button"
                variant="invisible"
              />
            </ActionMenu.Anchor>
            <ActionMenu.Overlay>
              <ActionList>
                {canAddMore ? (
                  <>
                    {!commentEnabled && (
                      <ActionList.Item
                        onSelect={() => onCommentEnabledChange(true)}
                        data-testid="add-action-post-comment"
                      >
                        Post comment
                        <ActionList.Description variant="block">Post comments on issues</ActionList.Description>
                      </ActionList.Item>
                    )}
                    {availableActions.map(type => {
                      const meta = ACTION_METADATA[type]
                      return (
                        <ActionList.Item key={type} onSelect={() => addAction(type)} data-testid={`add-action-${type}`}>
                          {meta.label}
                          <ActionList.Description variant="block">{meta.description}</ActionList.Description>
                        </ActionList.Item>
                      )
                    })}
                  </>
                ) : (
                  <ActionList.Item disabled data-testid="no-actions-available">
                    All actions added
                  </ActionList.Item>
                )}
              </ActionList>
            </ActionMenu.Overlay>
          </ActionMenu>
        }
      />

      {showList && (
        <div className={styles.cardList}>
          {/* Post comment */}
          {commentEnabled && (
            <div className={styles.actionCardRow} data-testid="action-post-comment">
              <div className={clsx(styles.cardContent, styles.cardContentInline)}>
                <p className={styles.cardTitle} id="action-label-add-comment">
                  Post comment
                </p>
                <p className={styles.cardDescription}>Post comments on issues</p>
              </div>
              <div className={styles.cardActions}>
                <IconButton
                  icon={TrashIcon}
                  aria-label="Remove Post comment"
                  variant="invisible"
                  size="small"
                  onClick={() => onCommentEnabledChange(false)}
                  data-testid="remove-action-post-comment"
                />
              </div>
            </div>
          )}

          {/* Configurable actions */}
          {enabledActions.map(action => {
            const meta = ACTION_METADATA[action.type]
            const isExpanded = expandedAction === action.type

            return (
              <div key={action.type} className={styles.actionCardRow} data-testid={`action-${action.type}`}>
                <div className={clsx(styles.cardContent, styles.cardContentInline)}>
                  <p className={styles.cardTitle} id={`action-label-${action.type}`}>
                    {meta.label}
                  </p>
                  <p className={styles.cardDescription}>{meta.description}</p>
                </div>
                <div className={styles.cardActions}>
                  <Button
                    variant="invisible"
                    size="small"
                    onClick={() => toggleAdvanced(action.type)}
                    aria-expanded={isExpanded}
                    data-testid={`action-advanced-${action.type}`}
                    trailingVisual={isExpanded ? ChevronDownIcon : ChevronRightIcon}
                  >
                    Advanced
                  </Button>
                  <IconButton
                    icon={TrashIcon}
                    aria-label={`Remove ${meta.label}`}
                    variant="invisible"
                    size="small"
                    onClick={() => removeAction(action.type)}
                    data-testid={`remove-action-${action.type}`}
                  />
                </div>
                <div
                  className={`${styles.advancedContent} ${isExpanded ? styles.advancedContentOpen : styles.advancedContentClosed}`}
                >
                  <div className={styles.advancedContentInner}>
                    {meta.allowedLabel && (
                      <div>
                        <p className={styles.fieldLabel}>{meta.allowedLabel}</p>
                        <AutocompleteTokenTagInput
                          values={action.allowed ?? []}
                          onChange={values => updateAction(action.type, {allowed: values})}
                          variant="allowed"
                          aria-label={meta.allowedLabel}
                          fetchSuggestions={fetchers[action.type]}
                          placeholder={searchText[action.type].placeholder}
                          emptyText={searchText[action.type].emptyText}
                        />
                      </div>
                    )}
                    {meta.blockedLabel && (
                      <div>
                        <p className={styles.fieldLabel}>{meta.blockedLabel}</p>
                        <AutocompleteTokenTagInput
                          values={action.blocked ?? []}
                          onChange={values => updateAction(action.type, {blocked: values})}
                          variant="blocked"
                          aria-label={meta.blockedLabel}
                          fetchSuggestions={fetchers[action.type]}
                          placeholder={searchText[action.type].placeholder}
                          emptyText={searchText[action.type].emptyText}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
